/**
 * InboxShell — top-level email client component.
 *
 * Tabs: Inbox (sidebar + list + detail), Priority Queue, Kanban, Calendar.
 * Folder sidebar only appears inside the Inbox tab.
 *
 * Uses absolute positioning for tab content to ensure full height —
 * Radix TabsContent renders display:block which breaks flex chains.
 */
import React, { useState } from 'react';
import { cn, EmptyState } from '../../ui';
import { InboxProvider, useInbox } from './InboxContext';
import { FolderSidebar } from './sidebar/FolderSidebar';
import { EmailList } from './list/EmailList';
import { EmailDetailHeader } from './detail/EmailDetailHeader';
import { EmailDetailBody } from './detail/EmailDetailBody';
import { PriorityQueueView } from './views/PriorityQueueView';
import { KanbanView } from './views/KanbanView';
import { CalendarView } from './views/CalendarView';
import { ComposeDialog } from './compose/ComposeDialog';
import { SmartInboxTab } from './smart/SmartInboxTab';
import { ICON_PX } from '../../ui';
import { Inbox as InboxIcon, ListChecks, Columns3, CalendarDays } from 'lucide-react';

const TAB_DEFS = [
  { key: 'inbox',    label: 'Inbox',          Icon: InboxIcon },
  { key: 'queue',    label: 'Priority Queue', Icon: ListChecks },
  { key: 'kanban',   label: 'Kanban',         Icon: Columns3 },
  { key: 'calendar', label: 'Calendar',       Icon: CalendarDays },
];

function InboxTab() {
  const { selectedEmail } = useInbox();
  const { header, list } = EmailList();

  return (
    <div style={{ display: 'flex', position: 'absolute', inset: 0 }}>
      {/* Folder sidebar */}
      <div style={{ width: 140, flexShrink: 0, borderRight: '1px solid var(--surface)', overflowY: 'auto' }}>
        <FolderSidebar />
      </div>

      {/* Email list */}
      <div style={{ width: '32%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--surface)', minWidth: 0 }}>
        <div style={{ flexShrink: 0, padding: 6, borderBottom: '1px solid var(--surface)' }}>
          {header}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list.length > 0 ? list : (
              <EmptyState
                icon={<InboxIcon className="h-6 w-6" />}
                title="No emails"
                body="This folder is empty or no emails match your filters."
                size="sm"
              />
            )}
          </div>
        </div>
      </div>

      {/* Detail pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {selectedEmail ? (
          <>
            <div style={{ flexShrink: 0, padding: '10px 16px', borderBottom: '1px solid var(--surface)' }}>
              <EmailDetailHeader email={selectedEmail} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              <EmailDetailBody email={selectedEmail} />
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState title="Select an email" body="Choose an email from the list to read it." size="md" />
          </div>
        )}
      </div>
    </div>
  );
}

function InboxContent({ style, className, variant }) {
  const [activeTab, setActiveTab] = useState('inbox');
  const { folderCounts, emails } = useInbox();

  // Badge counts
  const inboxUnread = folderCounts.unread.inbox ?? 0;
  const queuedToday = emails.filter((e) => (e.queueStatus === 'queued' || e.queueStatus === 'scheduled') && e.boardSwimlane === 'today').length;

  const badgeCounts = {
    inbox: inboxUnread,
    queue: queuedToday,
  };

  return (
    <div
      className={cn('flex flex-col rounded-lg border border-nn-border bg-nn-white overflow-hidden', className)}
      style={style}
    >
      {/* Tab bar */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 0, borderBottom: '1px solid var(--surface)', paddingLeft: 8 }}>
        {TAB_DEFS.map(({ key, label, Icon }) => {
          const active = activeTab === key;
          const count = badgeCounts[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 13, fontWeight: active ? 600 : 500,
                padding: '8px 14px',
                border: active ? '1px solid var(--surface)' : '1px solid transparent',
                borderBottom: active ? '1px solid var(--white)' : '1px solid transparent',
                borderRadius: '6px 6px 0 0',
                marginBottom: -1,
                background: active ? 'var(--white)' : 'transparent',
                color: active ? 'var(--aqua)' : 'var(--text-dark-secondary)',
                cursor: 'pointer',
                transition: 'color 0.15s',
              }}
            >
              <Icon size={ICON_PX.md} />
              {label}
              {count > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, lineHeight: 1,
                  padding: '2px 5px', borderRadius: 10,
                  background: key === 'inbox' ? 'var(--red-critical)' : 'var(--green)',
                  color: 'white',
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content — relative container, child uses position:absolute to fill */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {activeTab === 'inbox' && (variant === 'smart' ? <SmartInboxTab /> : <InboxTab />)}
        {activeTab === 'queue' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <PriorityQueueView />
          </div>
        )}
        {activeTab === 'kanban' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            <KanbanView />
          </div>
        )}
        {activeTab === 'calendar' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            <CalendarView />
          </div>
        )}
      </div>

      {/* Compose modal — renders when compose state is set */}
      <ComposeDialog />
    </div>
  );
}

function InboxShell({ style, className, variant = 'full' }) {
  return (
    <InboxProvider>
      <InboxContent style={style} className={className} variant={variant} />
    </InboxProvider>
  );
}

export default InboxShell;
